import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  Mail,
  LogOut,
  Check,
  Archive,
  RotateCcw,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import AppHeader from "../components/app/AppHeader";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { managePlan, managePlanLabel } from "../lib/billing";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

function SectionCard({ title, description, children, testId }) {
  return (
    <section
      className="rounded-[12px] border"
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
        padding: 24,
      }}
      data-testid={testId}
    >
      <div className="mb-5">
        <h2
          className="text-text-primary"
          style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.005em" }}
        >
          {title}
        </h2>
        {description && (
          <p
            className="text-text-secondary mt-1"
            style={{ fontSize: 13, lineHeight: 1.55 }}
          >
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, action }) {
  return (
    <div
      className="flex items-center justify-between border-t pt-4 mt-4 first:border-0 first:pt-0 first:mt-0"
      style={{ borderColor: "var(--color-border-secondary)" }}
    >
      <div className="min-w-0">
        <p
          className="font-mono text-text-tertiary mb-1"
          style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}
        >
          {label}
        </p>
        <p
          className="text-text-primary truncate"
          style={{ fontSize: 14, fontWeight: 500 }}
        >
          {value}
        </p>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

function Toggle({ enabled, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      data-testid={testId}
      className="motion-micro relative"
      style={{
        width: 44,
        height: 24,
        borderRadius: 9999,
        background: enabled ? "var(--color-accent)" : "var(--color-background-tertiary)",
        border: `1px solid ${enabled ? "var(--color-accent)" : "var(--color-border-primary)"}`,
        cursor: "pointer",
      }}
    >
      <span
        aria-hidden
        className="motion-micro absolute top-1/2 -translate-y-1/2"
        style={{
          width: 18,
          height: 18,
          borderRadius: 9999,
          background: enabled ? "#0a0a0a" : "var(--color-text-secondary)",
          left: enabled ? 22 : 2,
          transition: "left 150ms ease",
        }}
      />
    </button>
  );
}

function GhostButton({ children, onClick, disabled, testId, danger, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="motion-micro inline-flex items-center gap-2"
      style={{
        background: "transparent",
        border: `1px solid ${danger ? "color-mix(in oklab, var(--color-danger) 45%, transparent)" : "var(--color-border-primary)"}`,
        color: danger ? "var(--color-danger)" : "var(--color-text-primary)",
        height: 36,
        padding: "0 14px",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.borderColor = "var(--color-danger)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = danger
          ? "color-mix(in oklab, var(--color-danger) 45%, transparent)"
          : "var(--color-border-primary)";
      }}
    >
      {children}
    </button>
  );
}

/**
 * Confirmation modal for the irreversible actions. `confirmWord`, when given,
 * requires the user to type it before the action unlocks; `requirePassword`
 * additionally collects the account password (re-authentication for deletion).
 */
function DangerDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  confirmWord,
  requirePassword,
  onConfirm,
  testId,
}) {
  const [typed, setTyped] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setTyped("");
      setPassword("");
      setSubmitting(false);
    }
  }, [open]);

  const wordOk = !confirmWord || typed.trim().toUpperCase() === confirmWord;
  const passwordOk = !requirePassword || password.length > 0;
  const ready = wordOk && passwordOk && !submitting;

  const inputStyle = {
    width: "100%",
    height: 40,
    background: "var(--color-background-tertiary)",
    border: "1px solid var(--color-border-primary)",
    color: "var(--color-text-primary)",
    borderRadius: 8,
    padding: "0 12px",
    fontSize: 13,
    outline: "none",
  };

  const submit = async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      await onConfirm({ password });
      onOpenChange(false);
    } catch {
      // The action reports its own failure; staying open lets the user retry
      // (e.g. after mistyping the password).
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testId}
        style={{
          background: "var(--color-background-secondary)",
          borderColor: "var(--color-border-primary)",
          borderRadius: 16,
        }}
      >
        <DialogHeader>
          <DialogTitle
            className="text-text-primary inline-flex items-center gap-2"
            style={{ fontSize: 18, fontWeight: 600 }}
          >
            <AlertTriangle size={18} color="var(--color-danger)" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-text-secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
          {body}
        </p>
        {confirmWord && (
          <div>
            <label
              className="block mb-2 text-text-secondary"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              Type <span className="font-mono text-text-primary">{confirmWord}</span> to confirm
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              data-testid={`${testId}-word`}
              autoComplete="off"
              style={inputStyle}
            />
          </div>
        )}
        {requirePassword && (
          <div>
            <label
              className="block mb-2 text-text-secondary"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              Confirm your password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={`${testId}-password`}
              autoComplete="current-password"
              style={inputStyle}
            />
          </div>
        )}
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid={`${testId}-cancel`}
            className="motion-micro"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ready}
            data-testid={`${testId}-confirm`}
            className="motion-micro"
            style={{
              background: "var(--color-danger)",
              color: "#fff",
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              border: "none",
              cursor: ready ? "pointer" : "not-allowed",
              opacity: ready ? 1 : 0.5,
            }}
          >
            {submitting ? "Working…" : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchivedRow({ project, onRestore, onDelete, busy }) {
  const when = project.archived_at || project.updated_at;
  return (
    <div
      className="flex items-center justify-between gap-3 border-t pt-3 mt-3 first:border-0 first:pt-0 first:mt-0"
      style={{ borderColor: "var(--color-border-secondary)" }}
      data-testid={`archived-row-${project.id}`}
    >
      <div className="min-w-0">
        <p className="text-text-primary truncate" style={{ fontSize: 13, fontWeight: 500 }}>
          {project.name}
        </p>
        <p
          className="font-mono text-text-tertiary mt-0.5"
          style={{ fontSize: 11, letterSpacing: "0.02em" }}
        >
          {(project.selected_provider || "").toUpperCase()} ·{" "}
          {(project.architecture || []).length} services
          {when ? ` · archived ${new Date(when).toLocaleDateString()}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <GhostButton
          onClick={() => onRestore(project)}
          disabled={busy}
          testId={`archived-restore-${project.id}`}
          style={{ height: 32, fontSize: 12 }}
        >
          <RotateCcw size={13} /> Restore
        </GhostButton>
        <GhostButton
          onClick={() => onDelete(project)}
          disabled={busy}
          danger
          testId={`archived-delete-${project.id}`}
          style={{ height: 32, fontSize: 12 }}
        >
          <Trash2 size={13} /> Delete
        </GhostButton>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [productUpdates, setProductUpdates] = useState(true);
  const [billingAlerts, setBillingAlerts] = useState(true);
  const [prefsDirty, setPrefsDirty] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  // null = not loaded yet; the section loads lazily so the page's first paint
  // isn't waiting on a list most users never open.
  const [archived, setArchived] = useState(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedBusy, setArchivedBusy] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [archivedDeleteTarget, setArchivedDeleteTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/me/usage"), api.get("/me/preferences")])
      .then(([usageRes, prefsRes]) => {
        if (cancelled) return;
        setUsage(usageRes.data);
        setProductUpdates(!!prefsRes.data?.product_updates);
        setBillingAlerts(!!prefsRes.data?.billing_alerts);
      })
      .catch(() => {
        // Best-effort — leave defaults in place.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSignOut = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  const onSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.patch("/me/preferences", {
        product_updates: productUpdates,
        billing_alerts: billingAlerts,
      });
      toast.success("Notification preferences saved");
      setPrefsDirty(false);
    } catch (e) {
      toast.error("Couldn't save preferences", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setSavingPrefs(false);
    }
  };

  const loadArchived = async () => {
    try {
      const { data } = await api.get("/projects/archived");
      setArchived(data);
      return data;
    } catch (e) {
      toast.error("Couldn't load archived builds", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
      setArchived([]);
      return [];
    }
  };

  const onToggleArchived = async () => {
    const next = !archivedOpen;
    setArchivedOpen(next);
    if (next && archived === null) await loadArchived();
  };

  const onRestore = async (project) => {
    setArchivedBusy(true);
    try {
      await api.post(`/projects/${project.id}/unarchive`);
      setArchived((prev) => (prev || []).filter((p) => p.id !== project.id));
      toast.success("Restored", { description: `${project.name} is back in your projects.` });
    } catch (e) {
      toast.error("Restore failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setArchivedBusy(false);
    }
  };

  const onDeleteArchived = async (project) => {
    setArchivedBusy(true);
    try {
      await api.delete(`/projects/${project.id}`);
      setArchived((prev) => (prev || []).filter((p) => p.id !== project.id));
      toast.success("Deleted", { description: project.name });
    } catch (e) {
      toast.error("Delete failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setArchivedBusy(false);
    }
  };

  const onClearAllBuilds = async () => {
    try {
      const { data } = await api.delete("/projects");
      setArchived([]);
      const n = data?.deleted ?? 0;
      toast.success(`Cleared ${n} architecture${n === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Couldn't clear builds", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  const onDeleteAccount = async ({ password }) => {
    try {
      await api.delete("/auth/account", { data: { password } });
      toast.success("Account deleted");
      // The server already cleared the auth cookies; this resets client state.
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error("Couldn't delete account", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
      throw e; // keeps the dialog open so the password can be corrected
    }
  };

  const updateProductUpdates = (v) => {
    setProductUpdates(v);
    setPrefsDirty(true);
  };
  const updateBillingAlerts = (v) => {
    setBillingAlerts(v);
    setPrefsDirty(true);
  };

  const memberSince = (() => {
    if (!user?.created_at) return "—";
    try {
      return new Date(user.created_at).toLocaleDateString(undefined, {
        month: "long",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  })();

  const planName = (usage?.plan || "free").toUpperCase();
  const planSummary = `${usage?.projects_used ?? 0} of ${usage?.projects_limit ?? "unlimited"} active projects`;

  return (
    <div className="min-h-screen bg-bg-primary" data-testid="settings-page">
      <AppHeader />

      <main className="max-w-[840px] mx-auto px-6 py-14">
        <header className="mb-10">
          <p
            className="font-mono text-text-tertiary mb-3"
            style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}
          >
            Workspace
          </p>
          <h1
            className="text-text-primary"
            style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.025em" }}
            data-testid="settings-headline"
          >
            Settings
          </h1>
          <p
            className="text-text-secondary mt-2"
            style={{ fontSize: 14, lineHeight: 1.55 }}
          >
            Manage your account, plan, and notification preferences.
          </p>
        </header>

        <div className="space-y-6">
          <SectionCard
            title="Account"
            description="Your identity on Guided Sentry."
            testId="settings-section-account"
          >
            <Row label="Email" value={user?.email || "—"} />
            <Row label="Display name" value={user?.display_name || user?.email?.split("@")[0] || "—"} />
            <Row label="Member since" value={memberSince} />
          </SectionCard>

          <SectionCard
            title="Plan"
            description="Your project allowance and billing settings."
            testId="settings-section-plan"
          >
            <Row
              label="Current plan"
              value={
                <span className="inline-flex items-center gap-2">
                  <span
                    className="font-mono"
                    style={{
                      padding: "3px 10px",
                      borderRadius: 9999,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      color: "var(--color-accent)",
                      background: "color-mix(in oklab, var(--color-accent) 14%, transparent)",
                      border: "1px solid color-mix(in oklab, var(--color-accent) 45%, transparent)",
                    }}
                    data-testid="settings-plan-pill"
                  >
                    {planName}
                  </span>
                  <span className="text-text-secondary" style={{ fontSize: 13 }}>
                    {planSummary}
                  </span>
                </span>
              }
              action={
                <button
                  type="button"
                  data-testid="settings-upgrade-link"
                  onClick={() => managePlan(usage, navigate)}
                  className="motion-micro inline-flex items-center gap-1.5"
                  style={{
                    background: "var(--color-accent)",
                    color: "#0a0a0a",
                    height: 36,
                    padding: "0 14px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    textDecoration: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-accent-hover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-accent)")}
                >
                  {managePlanLabel(usage)} <ArrowRight size={14} strokeWidth={2.25} />
                </button>
              }
            />
          </SectionCard>

          <SectionCard
            title="Notifications"
            description="What we email you about. We never share your address."
            testId="settings-section-notifications"
          >
            <div
              className="flex items-center justify-between border-b pb-4"
              style={{ borderColor: "var(--color-border-secondary)" }}
            >
              <div className="flex items-start gap-3">
                <Sparkles size={16} color="var(--color-accent)" className="mt-0.5" />
                <div>
                  <p
                    className="text-text-primary"
                    style={{ fontSize: 13, fontWeight: 500 }}
                  >
                    Product updates
                  </p>
                  <p
                    className="text-text-tertiary mt-0.5"
                    style={{ fontSize: 12 }}
                  >
                    New templates, features, and changelog.
                  </p>
                </div>
              </div>
              <Toggle
                enabled={productUpdates}
                onChange={updateProductUpdates}
                testId="settings-toggle-product-updates"
              />
            </div>
            <div className="flex items-center justify-between pt-4">
              <div className="flex items-start gap-3">
                <Mail size={16} color="var(--color-accent)" className="mt-0.5" />
                <div>
                  <p
                    className="text-text-primary"
                    style={{ fontSize: 13, fontWeight: 500 }}
                  >
                    Billing &amp; usage alerts
                  </p>
                  <p
                    className="text-text-tertiary mt-0.5"
                    style={{ fontSize: 12 }}
                  >
                    Receipts and a reminder when you&apos;re close to your monthly architecture-run limit.
                  </p>
                </div>
              </div>
              <Toggle
                enabled={billingAlerts}
                onChange={updateBillingAlerts}
                testId="settings-toggle-billing-alerts"
              />
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={onSavePrefs}
                disabled={!prefsDirty || savingPrefs}
                data-testid="settings-save-prefs"
                className="motion-micro inline-flex items-center gap-1.5"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border-primary)",
                  color: prefsDirty ? "var(--color-text-primary)" : "var(--color-text-tertiary)",
                  height: 36,
                  padding: "0 14px",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: !prefsDirty || savingPrefs ? "not-allowed" : "pointer",
                  opacity: !prefsDirty || savingPrefs ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (prefsDirty && !savingPrefs)
                    e.currentTarget.style.borderColor = "var(--color-accent)";
                }}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--color-border-primary)")}
              >
                <Check size={14} strokeWidth={2.25} />
                {savingPrefs ? "Saving…" : "Save preferences"}
              </button>
            </div>
          </SectionCard>

          <SectionCard
            title="Archived architectures"
            description="Builds you've archived from the projects page. Restore one to put it back, or delete it for good."
            testId="settings-section-archived"
          >
            <GhostButton onClick={onToggleArchived} testId="settings-view-archived">
              <Archive size={14} />
              {archivedOpen ? "Hide archived builds" : "View archived builds"}
              {archived !== null && archived.length > 0 ? ` (${archived.length})` : ""}
            </GhostButton>

            {archivedOpen && (
              <div className="mt-5" data-testid="settings-archived-list">
                {archived === null ? (
                  <p className="text-text-tertiary" style={{ fontSize: 13 }}>
                    Loading…
                  </p>
                ) : archived.length === 0 ? (
                  <p
                    className="text-text-tertiary"
                    style={{ fontSize: 13 }}
                    data-testid="settings-archived-empty"
                  >
                    Nothing archived yet.
                  </p>
                ) : (
                  archived.map((p) => (
                    <ArchivedRow
                      key={p.id}
                      project={p}
                      busy={archivedBusy}
                      onRestore={onRestore}
                      onDelete={setArchivedDeleteTarget}
                    />
                  ))
                )}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Danger zone"
            description="These actions are permanent and cannot be undone."
            testId="settings-section-danger"
          >
            <div
              className="flex items-center justify-between gap-4 flex-wrap"
              style={{ marginBottom: 16 }}
            >
              <div className="min-w-0">
                <p className="text-text-primary" style={{ fontSize: 13, fontWeight: 500 }}>
                  Clear all architecture builds
                </p>
                <p className="text-text-tertiary mt-0.5" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  Deletes every architecture on this account, archived ones included. Your account
                  and plan stay as they are.
                </p>
              </div>
              <GhostButton onClick={() => setClearOpen(true)} danger testId="settings-clear-builds">
                <Trash2 size={14} /> Clear all builds
              </GhostButton>
            </div>

            <div
              className="flex items-center justify-between gap-4 flex-wrap border-t pt-4"
              style={{ borderColor: "var(--color-border-secondary)" }}
            >
              <div className="min-w-0">
                <p className="text-text-primary" style={{ fontSize: 13, fontWeight: 500 }}>
                  Delete account permanently
                </p>
                <p className="text-text-tertiary mt-0.5" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  Removes your account, every architecture, and every saved template. If you have a
                  paid subscription, cancel it in the billing portal first — deleting here does not
                  cancel it.
                </p>
              </div>
              <GhostButton
                onClick={() => setDeleteAccountOpen(true)}
                danger
                testId="settings-delete-account"
              >
                <Trash2 size={14} /> Delete account
              </GhostButton>
            </div>
          </SectionCard>

          <SectionCard
            title="Sign out"
            description="End your session on this browser."
            testId="settings-section-signout"
          >
            <button
              type="button"
              onClick={onSignOut}
              data-testid="settings-signout-btn"
              className="motion-micro inline-flex items-center gap-2"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-primary)",
                height: 40,
                padding: "0 16px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--color-danger)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--color-border-primary)")}
            >
              <LogOut size={14} strokeWidth={2} /> Sign out
            </button>
          </SectionCard>
        </div>
      </main>

      <DangerDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        testId="clear-builds-dialog"
        title="Clear all architecture builds?"
        body="Every architecture on this account — including archived ones — will be permanently deleted. Saved templates and your account are not affected."
        confirmWord="CLEAR"
        confirmLabel="Clear all builds"
        onConfirm={onClearAllBuilds}
      />

      <DangerDialog
        open={deleteAccountOpen}
        onOpenChange={setDeleteAccountOpen}
        testId="delete-account-dialog"
        title="Delete your account?"
        body={`This permanently deletes ${user?.email || "your account"}, all of your architectures, and all of your saved templates. This cannot be undone.`}
        confirmWord="DELETE"
        requirePassword
        confirmLabel="Delete my account"
        onConfirm={onDeleteAccount}
      />

      <DangerDialog
        open={!!archivedDeleteTarget}
        onOpenChange={(v) => !v && setArchivedDeleteTarget(null)}
        testId="delete-archived-dialog"
        title="Delete this archived build?"
        body={`${archivedDeleteTarget?.name || "This architecture"} will be permanently removed. Restore it instead if you might want it back.`}
        confirmLabel="Delete permanently"
        onConfirm={async () => {
          const target = archivedDeleteTarget;
          setArchivedDeleteTarget(null);
          if (target) await onDeleteArchived(target);
        }}
      />
    </div>
  );
}
